import { useState, useEffect } from "react";
import { Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useResource } from "../lib/hooks";
import { aud, api } from "../lib/api";
import { PageHeader, Pill, statusTone } from "../components/Common";
import { RecordDialog } from "../components/RecordDialog";
import { Button } from "../components/ui/button";

function StageEditor({ pipeline, onSave, onClose }) {
  const [name, setName] = useState(pipeline.name || "");
  const [stages, setStages] = useState((pipeline.stages || []).map((s) => s.name).join("\n"));
  const fields = [{ key: "name", label: "Pipeline name", required: true, full: true }, { key: "stages", label: "Stages (one per line)", type: "textarea", rows: 6 }];
  return (
    <RecordDialog open onOpenChange={(o) => !o && onClose()} title={pipeline.id ? "Edit pipeline" : "New pipeline"} fields={fields}
      initial={{ name, stages }} onSave={async (f) => {
        setName(f.name); setStages(f.stages);
        const old = pipeline.stages || [];
        const st = String(f.stages || "").split("\n").map((s) => s.trim()).filter(Boolean)
          .map((n) => old.find((o) => o.name === n) || { id: crypto.randomUUID(), name: n });
        await onSave({ name: f.name, stages: st }, pipeline.id);
      }} />
  );
}

export default function Pipelines() {
  const pipes = useResource("pipelines");
  const contacts = useResource("contacts");
  const [pid, setPid] = useState(null);
  const pipeline = pipes.items.find((p) => p.id === pid) || pipes.items[0];
  const opps = useResource("opportunities", pipeline ? { pipeline_id: pipeline.id } : { pipeline_id: "none" });
  const [edit, setEdit] = useState(null);
  const [stageEdit, setStageEdit] = useState(null);
  const [drag, setDrag] = useState(null);
  useEffect(() => { if (pipeline && !pid) setPid(pipeline.id); }, [pipeline, pid]);

  const move = async (opp, stage) => {
    if (opp.stage_id === stage.id) return;
    opps.setItems((it) => it.map((o) => (o.id === opp.id ? { ...o, stage_id: stage.id } : o)));
    await api.put(`/r/opportunities/${opp.id}`, { stage_id: stage.id, stage_name: stage.name });
    toast.success(`Moved to ${stage.name}`);
  };

  const fields = pipeline ? [
    { key: "name", label: "Opportunity name", required: true },
    { key: "contact_id", label: "Contact", type: "select", options: contacts.items.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}` })) },
    { key: "value", label: "Value (AUD)", type: "number" },
    { key: "stage_id", label: "Stage", type: "select", required: true, options: pipeline.stages.map((s) => ({ value: s.id, label: s.name })) },
    { key: "status", label: "Status", type: "select", options: ["open", "won", "lost", "abandoned"] },
    { key: "close_date", label: "Expected close", type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
  ] : [];

  const saveOpp = async (f) => {
    const c = contacts.items.find((x) => x.id === f.contact_id);
    await opps.save({ status: "open", ...f, pipeline_id: pipeline.id, contact_name: c ? `${c.first_name} ${c.last_name || ""}` : f.contact_name }, edit.id);
    toast.success("Opportunity saved");
  };

  return (
    <div>
      <PageHeader overline="Sales" title="Opportunity Pipelines" desc="Drag deals between stages. Stage changes can trigger workflows.">
        <select data-testid="pipeline-select" value={pipeline?.id || ""} onChange={(e) => setPid(e.target.value)} className="h-9 rounded-md border border-input bg-card px-3 text-sm">
          {pipes.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button variant="secondary" onClick={() => setStageEdit(pipeline)} data-testid="edit-pipeline-btn"><Settings2 className="h-4 w-4 mr-1" />Stages</Button>
        <Button variant="outline" onClick={() => setStageEdit({})} data-testid="new-pipeline-btn">New pipeline</Button>
        <Button onClick={() => setEdit({ stage_id: pipeline?.stages[0]?.id })} data-testid="new-opportunity-btn" disabled={!pipeline}><Plus className="h-4 w-4 mr-1" />Opportunity</Button>
      </PageHeader>
      {pipeline && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {pipeline.stages.map((s) => {
            const list = opps.items.filter((o) => o.stage_id === s.id);
            return (
              <div key={s.id} className="w-72 shrink-0 rounded-md border bg-card/50" data-testid={`stage-col-${s.id}`}
                onDragOver={(e) => e.preventDefault()} onDrop={() => drag && move(drag, s)}>
                <div className="p-3 border-b flex justify-between items-center">
                  <span className="font-display font-bold">{s.name}</span>
                  <span className="text-xs font-mono text-muted-foreground">{list.length} · {aud(list.reduce((a, o) => a + Number(o.value || 0), 0))}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[300px]">
                  {list.map((o) => (
                    <div key={o.id} draggable onDragStart={() => setDrag(o)} onClick={() => setEdit(o)} data-testid={`opp-card-${o.id}`}
                      className="rounded border bg-background p-3 cursor-grab hover:border-primary/60 transition-colors">
                      <div className="text-sm font-medium">{o.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">{o.contact_name || "No contact"}</div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="font-mono text-sm text-primary">{aud(o.value)}</span>
                        <Pill tone={statusTone(o.status)}>{o.status}</Pill>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit opportunity" : "New opportunity"} fields={fields} initial={edit} onSave={saveOpp} />}
      {stageEdit && <StageEditor pipeline={stageEdit} onClose={() => setStageEdit(null)} onSave={async (b, id) => { const p = await pipes.save(b, id); setPid(p.id); toast.success("Pipeline saved"); }} />}
    </div>
  );
}
